import { Component, createEffect, createSignal, onMount } from 'solid-js'
import { galleryStore } from '/web/store/gallery'
import { getAssetUrl } from '/web/shared/util'
import { NoTitleModal } from '/web/shared/Modal'
import { characterStore } from '/web/store'
import { AppSchema } from '/common/types'
import HeaderContainer from '/web/shared/aifans/HeaderContainer'
import ChatLeftNavContainer from './Chat/ChatLeftNavContainer'
import { Title, Meta } from '@solidjs/meta'

const Gallery: Component = () => {
  const [image, setImage] = createSignal<string | undefined>(undefined)
  const [charactersInFilter, setCharactersInFilter] = createSignal<AppSchema.Character[]>([])
  const [selectedCharacterFilter, setSelectedCharacterFilter] = createSignal<string | undefined>(
    undefined
  )

  const gallery = galleryStore((s) => ({
    images: s.images,
    loading: s.loading,
  }))

  // character store
  const chars = characterStore((s) => ({
    list: s.characters.list,
    map: s.characters.list.reduce<Record<string, AppSchema.Character>>(
      (prev, curr) => Object.assign(prev, { [curr._id]: curr }),
      {}
    ),
    loaded: s.characters.loaded,
  }))

  onMount(async () => {
    if (!gallery.loading) {
      await galleryStore.getGallery()
    }

    if (!chars.loaded) {
      await characterStore.getCharacters()
    }
  })

  createEffect(() => {
    if (gallery.images?.length) {
      const characterIdCounts = gallery.images.reduce((acc: any, image) => {
        // If the characterId is already in the accumulator, increment its count
        if (acc[image.characterId]) {
          acc[image.characterId]++
        } else {
          // If the characterId is not in the accumulator, add it with a count of 1
          acc[image.characterId] = 1
        }
        return acc
      }, {})

      const characterData = chars.list
        .map((character) => {
          const count = characterIdCounts[character._id] || 0
          return {
            ...character,
            count,
          }
        })
        .filter((character) => character.count > 0)

      setCharactersInFilter(characterData)
    }
  })

  return (
    <>
      <Title>CosplayFans - Gallery | Explore Stunning Cosplay Creations</Title>
      <Meta name="og:title" content="CosplayFans - Gallery" />
      <Meta
        name="description"
        content="View your personal collection of AI-generated cosplay images. Access and manage all your custom character creations in one place."
      />
      <Meta
        name="og:description"
        content="View your personal collection of AI-generated cosplay images. Access and manage all your custom character creations in one place."
      />
      <div class="relative h-full w-full bg-black">
        <HeaderContainer />
        <ChatLeftNavContainer />
        <div
          id="main"
          class="flex flex-1 flex-col pt-14 md:min-h-[calc(100vh-82px)] md:pt-0 lg:ml-[222px]"
        >
          <div class="flex flex-col px-8 pt-4 md:mt-[82px] md:flex-row">
            <span class="flex flex-1 font-clash text-[28px] font-bold md:hidden md:text-[40px]">
              Gallery
            </span>
            <div class="flex flex-shrink-0 items-start justify-start md:mr-5">
              <GalleryCharacterFilter
                characters={charactersInFilter()}
                onSelect={(id) => setSelectedCharacterFilter(id)}
                selectedId={selectedCharacterFilter()}
              />
            </div>
            <div class="w-full">
              <span class="hidden font-clash text-[28px] font-bold md:flex md:flex-1 md:text-[40px]">
                Gallery
              </span>

              <div class="mx-auto mb-5 grid grid-cols-2 gap-3 sm:gap-5 lg:grid-cols-2 xl:grid-cols-4 2xl:grid-cols-4">
                {gallery?.images
                  ?.filter((image) => {
                    if (!selectedCharacterFilter()) {
                      return true
                    }

                    if (image.characterId === selectedCharacterFilter()) {
                      return true
                    }

                    return false
                  })
                  .map((image) => (
                    <button onClick={() => setImage(image.url)}>
                      <div class="relative h-[250px] max-w-[400px] overflow-hidden rounded-lg sm:h-[320px] md:h-[380px]">
                        <img
                          class="absolute h-full w-full object-cover"
                          src={getAssetUrl(image.url)}
                          alt="user"
                        />
                      </div>
                    </button>
                  ))}
              </div>
            </div>
          </div>
        </div>
      </div>
      <NoTitleModal show={!!image()} close={() => setImage()} maxWidth="half">
        <div class="flex justify-center p-4">
          <img class="rounded-md" src={getAssetUrl(image() || '')} />
        </div>
      </NoTitleModal>
    </>
  )
}

const GalleryCharacterFilter = (props: {
  characters: AppSchema.Character[]
  selectedId?: string
  onSelect: (id?: string) => void
}) => {
  const [showFilters, setShowFilters] = createSignal(false)

  const bgClass = 'bg-cosplay-gray-600'
  const bgClassSelected = 'bg-gradient-to-r from-cosplay-purple to-cosplay-blue-200'
  return (
    <div class="mb-5 flex flex-1 flex-col gap-2 rounded-md bg-cosplay-gray-700 px-8 py-3 text-[17px] md:mb-0 md:flex-none  md:py-6">
      <button onClick={() => setShowFilters(!showFilters())} class="font-bold md:cursor-default">
        Character Filter
      </button>
      <div class={`${showFilters() ? 'flex' : 'hidden'} flex-col gap-2 md:flex`}>
        <button
          onClick={() => props.onSelect()}
          class={`rounded-md px-4 py-2 ${!props.selectedId ? bgClassSelected : bgClass}`}
        >
          All Characters
        </button>
        {props.characters
          ?.sort((a, b) => {
            return a.name.localeCompare(b.name, 'en', { numeric: true })
          })
          .map((c: any) => (
            <button
              onClick={() => props.onSelect(c._id)}
              class={`rounded-md bg-cosplay-gray-600 px-4 py-2 ${
                props.selectedId === c._id ? bgClassSelected : bgClass
              }`}
            >{`${c.name} ( ${c.count} )`}</button>
          ))}
      </div>
    </div>
  )
}

export default Gallery
